"use client";

import { HorizontalScroll } from "@/components/HorizontalScroll";
import { Button } from "@/components/ui/button";
import { Action, useAnalytics } from "@/context/AnalyticsProvider";
import { usePassportProvider } from "@/context/PassportProvider";
import { useWishlistHandler } from "@/hooks/useWishlistHandler";
import { notifyError } from "@/utils/monitoring";
import { Heart, Loader2 } from "lucide-react";
import Image from "next/image";
import { GemsBadge } from "../quest-badges";
import { polygonGames } from "./data";
import { GameCard } from "./game-card";

const PolygonLogo = () => {
  return (
    <div className="w-[200px] sm:w-[250px] lg:w-[307px] h-[40px] sm:h-[50px] lg:h-[60px] relative">
      <Image
        src="/images/polygon-games/polygon-logo.webp"
        alt="Polygon"
        fill
        className="object-contain"
      />
    </div>
  );
};

const ImmutableLogo = () => {
  return (
    <div className="w-[40px] sm:w-[50px] lg:w-[60px] h-[40px] sm:h-[50px] lg:h-[60px] relative">
      <Image
        src="/images/polygon-games/immutable-logo.webp"
        alt="Immutable"
        fill
        className="object-contain"
      />
    </div>
  );
};

const CrossoverIcon = () => {
  return (
    <div className="w-[24px] h-[24px] mx-5 relative">
      <Image
        src="/images/polygon-games/crossover-icon.webp"
        alt="Crossover"
        fill
        className="object-contain"
      />
    </div>
  );
};

const SocialStats = () => {
  return (
    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 lg:gap-8 border-t border-b border-white/10 py-3 sm:py-4">
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-8 w-full lg:w-auto">
        <a
          className="flex items-center gap-2 whitespace-nowrap hover:opacity-90 transition"
          href="https://x.com/0xPolygon"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="w-4 h-4 relative flex-shrink-0">
            <Image
              src="/images/polygon-games/x-logo.webp"
              alt="X (Twitter)"
              width={16}
              height={16}
              className="object-contain"
            />
          </div>
          <span className="text-xs sm:text-sm text-white/80">@0xPolygon</span>
          <span className="text-xs sm:text-sm text-white/80">2M Followers</span>
        </a>
        <a
          className="flex items-center gap-2 whitespace-nowrap hover:opacity-90 transition"
          href="https://t.me/PolygonHQ"
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="w-4 h-4 relative flex-shrink-0">
            <Image
              src="/images/quests/telegram-logo.svg"
              alt="Telegram"
              width={16}
              height={16}
              className="object-contain"
            />
          </div>
          <span className="text-xs sm:text-sm text-white/80">@PolygonHQ</span>
          <span className="text-xs sm:text-sm text-white/80">866 Members</span>
        </a>
      </div>
      <div className="flex items-center gap-2 whitespace-nowrap">
        <div className="w-4 h-4 relative flex-shrink-0">
          <Image
            src="/images/polygon-games/active-players-icon.webp"
            alt="Telegram"
            width={16}
            height={16}
            className="object-contain"
          />
        </div>
        <span className="text-xs sm:text-sm text-white/80">23k</span>
        <span className="text-xs sm:text-sm text-white/80">Active Players</span>
      </div>
    </div>
  );
};

const HeaderSection = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-10 py-4 sm:py-6 lg:py-8">
      <div className="flex flex-col gap-4 sm:gap-6 lg:gap-8">
        <div className="flex items-center">
          <ImmutableLogo />
          <CrossoverIcon />
          <PolygonLogo />
        </div>
        <SocialStats />
      </div>
    </div>
  );
};

const HeroContent = () => {
  return (
    <div className="space-y-3 sm:space-y-4">
      <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[48px] font-bold text-white leading-tight sm:leading-tight md:leading-tight lg:leading-[48px]">
        Exclusive Polygon Games & Prizes
        <br />
        Coming to Immutable Play.
      </h1>
      <p className="text-sm sm:text-base md:text-lg lg:text-[18px] font-light text-white/80 leading-relaxed sm:leading-relaxed md:leading-relaxed lg:leading-[28px] max-w-full lg:max-w-[905px]">
        The Battle For Legendary Loot Starts Soon!
      </p>
    </div>
  );
};

const HeroCTAButton = () => {
  const { trackV3 } = useAnalytics();
  const { isLoggedIn } = usePassportProvider();

  const gameId = "db5903f3-6d8d-4d52-b12a-d891fba6ea65";

  const { addToWishlist, isWishlisting, isWishlisted } = useWishlistHandler(
    gameId,
    10,
  );

  const handleWishlistQuest = async () => {
    if (isWishlisted) return;

    try {
      trackV3({
        object: "Quest",
        action: Action.COMPLETED,
        extras: {
          questName: "Follow Polygon Games",
          questType: "wishlist",
          gameId: gameId,
          gameName: "Polygon",
          section: "PolygonPage",
          isLoggedIn: Boolean(isLoggedIn),
        },
      });

      await addToWishlist();
    } catch (error) {
      notifyError(
        new Error(`Failed to complete Polygon wishlist quest: ${error}`),
        "wishlist",
      );
    }
  };

  return (
    <div className="flex items-center gap-3 sm:gap-4 lg:gap-5">
      <Button
        size="lg"
        className={`h-10 sm:h-12 lg:h-14 px-4 sm:px-5 lg:px-6 rounded-xl shadow-lg w-full sm:w-auto bg-white text-black hover:bg-white/90 ${isWishlisted ? "pointer-events-none" : ""}`}
        onClick={handleWishlistQuest}
        disabled={isWishlisting}
      >
        <div className="flex items-center gap-3 sm:gap-4 lg:gap-5">
          {isWishlisting ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Heart
              className={`w-5 h-5 ${isWishlisted ? "fill-current" : ""}`}
            />
          )}
          <span className="text-sm sm:text-base lg:text-lg font-medium">
            {isWishlisted ? "Following" : "Follow Polygon Games"}
          </span>
          {!isWishlisted && (
            <div className="flex items-center scale-125 px-1">
              <GemsBadge gems={10} />
            </div>
          )}
        </div>
      </Button>
    </div>
  );
};

const HeroSection = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10 transition-all duration-200 ease-out">
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-10">
        <div className="flex-1 space-y-6 sm:space-y-8 lg:space-y-10">
          <HeroContent />
          <HeroCTAButton />
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title }: { title: string }) => {
  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="h-px bg-white/20" />
      <div>
        <h2 className="text-xl sm:text-2xl lg:text-[30px] font-bold text-white">
          {title}
        </h2>
      </div>
    </div>
  );
};

// Styles to customize HorizontalScroll: smaller gap on mobile, scroll-snap for card alignment
const carouselStyles = [
  "[&>div>button]:!z-20", // Ensure nav buttons are above cards
  "[&>div>div]:!gap-4 sm:[&>div>div]:!gap-6", // Responsive gap
  "[&>div>div]:snap-x [&>div>div]:snap-mandatory", // Enable scroll-snap
  "[&>div>div>*]:snap-start", // Cards snap to start
].join(" ");

const GamesCarousel = () => {
  return (
    <div className={`w-full overflow-hidden relative ${carouselStyles}`}>
      <HorizontalScroll>
        {polygonGames.map((game) => (
          <GameCard key={game.id} game={game} />
        ))}
      </HorizontalScroll>
    </div>
  );
};

const GamesSection = () => {
  return (
    <div className="pl-4 sm:pl-6 lg:pl-10 py-6 sm:py-8">
      <div className="space-y-4 sm:space-y-6">
        <SectionHeader title="Polygon Games Coming Soon" />
        <GamesCarousel />
      </div>
    </div>
  );
};

export function PolygonPageContent() {
  return (
    <div className="min-h-screen text-white overflow-x-hidden relative">
      <div className="flex flex-col lg:flex-row max-w-full">
        <div className="flex-1 min-w-0">
          <HeaderSection />
          <HeroSection />
          <GamesSection />
        </div>
      </div>
    </div>
  );
}
